import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as ContentCopyIcon,
    DeleteOutline as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Layers as LayersIcon,
    Home as HomeIcon,
} from '@mui/icons-material';
import AutocompleteSelectFilter from '../AutocompleteSelectFilter/AutocompleteSelectFilter';
import styles from './DocumentSettingsDialog.module.scss';
import { BASE_DESIGN_ID, getStyleDisplayName } from '../../../Utils/DocumentVersion/documentDesignVersions';

/**
 * Base design row + a picker of the saved versions (the base is not in the picker), with
 * rename / duplicate / delete for the selected version.
 * Each version has its own theme; several versions may share one theme.
 */
const DesignVersionsPanel = ({
    t,
    direction,
    baseStyleId,
    versions,
    activeTargetId,
    isDirty,
    saving,
    onSelect,
    onCreate,
    onRename,
    onDuplicate,
    onDelete,
    onSave,
}) => {
    const tr = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };

    const isBaseActive = activeTargetId === BASE_DESIGN_ID;
    const versionName = (version, index) => version.name || `${tr('DesignVersion', 'גרסה')} ${index + 1}`;
    const versionOptions = versions.map((version, index) => ({
        value: version.id,
        label: `${index + 1}. ${versionName(version, index)} · ${getStyleDisplayName(version.documentStyleVersion, t)}`,
    }));
    const activeIndex = versions.findIndex((v) => v.id === activeTargetId);
    const activeVersion = activeIndex >= 0 ? versions[activeIndex] : null;
    const dirtyTag = <span className={styles.versionDirtyTag}>{tr('UnsavedChanges', 'לא נשמר')}</span>;

    return (
        <div className={styles.sectionBlock} dir={direction}>
            <div className={styles.versionsHeader}>
                <div className={styles.sectionHeading}>
                    <LayersIcon className={styles.sectionHeadingIcon} />
                    {tr('DesignVersions', 'גרסאות עיצוב')}
                </div>
                <div className={styles.versionsHeaderActions}>
                    <Tooltip title={tr('SaveDesignVersion', 'שמור גרסה')}>
                        <span>
                            <IconButton size="small" onClick={onSave} disabled={saving} className={styles.versionActionBtn}>
                                <SaveIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <button type="button" className={styles.versionAddBtn} onClick={onCreate} disabled={saving}>
                        <AddIcon fontSize="small" />
                        {tr('NewDesignVersion', 'גרסה חדשה')}
                    </button>
                </div>
            </div>
            <span className={styles.hint}>
                {tr('DesignVersionsHint', 'כל גרסה שומרת את כל ההגדרות שלה: עיצוב, פרטי כותרת, צבעים, גופנים, לוגו, עמודות ותיאורים. בעת הפקת מסמך בוחרים באיזו גרסה להשתמש.')}
            </span>

            <ul className={styles.versionList}>
                <li className={`${styles.versionItem}${isBaseActive ? ` ${styles.versionItemActive}` : ''}`}>
                    <button
                        type="button"
                        className={styles.versionSelect}
                        onClick={() => onSelect(BASE_DESIGN_ID)}
                        aria-pressed={isBaseActive}
                    >
                        <span className={styles.versionIndex}><HomeIcon fontSize="inherit" /></span>
                        <span className={styles.versionName}>{tr('BaseDesign', 'עיצוב בסיסי')}</span>
                        <span className={styles.versionDefaultTag}>{getStyleDisplayName(baseStyleId, t)}</span>
                        {isBaseActive && isDirty && dirtyTag}
                    </button>
                </li>
            </ul>

            {versions.length > 0 && (
                <div className={styles.versionPicker}>
                    <div className={styles.fieldTitle}>{tr('SavedDesignVersions', 'גרסאות שמורות')}</div>
                    <AutocompleteSelectFilter
                        options={versionOptions}
                        selectedValues={versionOptions.find((o) => o.value === activeTargetId) || null}
                        onChange={(_, option) => onSelect(option?.value || BASE_DESIGN_ID)}
                        placeholder={tr('SelectDesignVersion', 'בחרו גרסה לעריכה')}
                        isMultiple={false}
                        direction={direction}
                        portalMenu
                        searchable
                    />
                    {activeVersion && (
                        <div className={`${styles.versionItem} ${styles.versionItemActive}`}>
                            <div className={styles.versionSelect}>
                                <span className={styles.versionIndex}>{activeIndex + 1}</span>
                                <span className={styles.versionName}>{versionName(activeVersion, activeIndex)}</span>
                                <span className={styles.versionDefaultTag}>
                                    {getStyleDisplayName(activeVersion.documentStyleVersion, t)}
                                </span>
                                {isDirty && dirtyTag}
                            </div>
                            <div className={styles.versionActions}>
                                <Tooltip title={tr('RenameDesignVersion', 'שנה שם')}>
                                    <IconButton size="small" onClick={() => onRename(activeVersion.id)} className={styles.versionActionBtn}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={tr('DuplicateDesignVersion', 'שכפל')}>
                                    <IconButton size="small" onClick={() => onDuplicate(activeVersion.id)} className={styles.versionActionBtn}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={tr('DeleteDesignVersion', 'מחק')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(activeVersion.id)}
                                        className={`${styles.versionActionBtn} ${styles.versionDeleteBtn}`}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <span className={styles.hint}>
                {tr('BaseDesignHint', 'העיצוב הבסיסי משמש כאשר לא נבחרה גרסה, וגם באפליקציה.')}
            </span>
        </div>
    );
};

export default DesignVersionsPanel;
